import React, { useState, useEffect, useCallback } from 'react';
import { shortLocation } from '../../../utils/locationDisplay';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Alert,
  Table,
  InputNumber,
  AutoComplete,
  Tag,
  Tooltip,
  message,
  Divider,
  Popconfirm,
  TreeSelect,
  DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  DeleteOutlined,
  WarningOutlined,
  HomeOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SwapOutlined,
  BoxPlotOutlined,
  ApartmentOutlined,
  BuildOutlined,
  BarsOutlined,
  DatabaseOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { InternalTransfer, TransferItem as BaseTransferItem, TransferStatus } from '../pages/InternalTransferPage';
import apiService from '../../../services/apiService';
import { makeSourceKey, parseSourceKey } from './sourceLocationKey';
import { useTranslation } from "react-i18next";
import type { TFunction } from 'i18next';

// 本地擴展的轉移項目接口，確保類型安全
interface TransferItem extends BaseTransferItem {
  quantity: number;
  currentCustomer?: string;
  targetCustomer?: string;
  currentWarehouse?: string;
  targetWarehouse?: string;
}

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 库位类型定义
type LocationType = 'ZONE' | 'FLOOR' | 'AISLE' | 'RACK' | 'SHELF' | 'BIN' | 'UNKNOWN';

// 库位接口
interface Location {
  id: string;
  name: string;
  code: string;
  warehouseId: string;
}

// 获取库位显示配置（与 Inbound 相同）
const getInboundLocationDisplayConfig = (
  node: any,
  t: TFunction
): { icon: React.ReactNode; color: string; label: string; type: LocationType } => {
  const rawType = node?.locationType ?? node?.location_type ?? node?.type ?? '';
  const upperType = rawType.toString().toUpperCase();

  if (upperType === 'ZONE') return { icon: <ApartmentOutlined />, color: 'blue', label: t('區域'), type: 'ZONE' };
  if (upperType === 'FLOOR') return { icon: <BuildOutlined />, color: 'gold', label: t('樓層'), type: 'FLOOR' };
  if (upperType === 'AISLE') return { icon: <BarsOutlined />, color: 'green', label: t('通道'), type: 'AISLE' };
  if (upperType === 'RACK') return { icon: <DatabaseOutlined />, color: 'orange', label: t('貨架'), type: 'RACK' };
  if (upperType === 'SHELF') return { icon: <BoxPlotOutlined />, color: 'purple', label: t('層'), type: 'SHELF' };
  if (upperType === 'BIN') return { icon: <HomeOutlined />, color: 'red', label: t('儲位'), type: 'BIN' };

  if (node?.bin) return { icon: <HomeOutlined />, color: 'red', label: t('儲位'), type: 'BIN' };
  if (node?.shelf) return { icon: <BoxPlotOutlined />, color: 'purple', label: t('層'), type: 'SHELF' };
  if (node?.rack) return { icon: <DatabaseOutlined />, color: 'orange', label: t('貨架'), type: 'RACK' };
  if (node?.aisle) return { icon: <BarsOutlined />, color: 'green', label: t('通道'), type: 'AISLE' };

  return { icon: <FolderOutlined />, color: 'default', label: upperType || t('其他'), type: 'UNKNOWN' };
};

interface Warehouse {
  value: string;
  label: string;
  locations: string[];
  id?: string;
  code?: string;
}

interface Product {
  id: string;
  name: string;
  nameCN: string;
  sku: string;
  availableStock: Record<string, number>;
  unit: string;
  currentLocation?: {
    warehouse: string;
    location: string;
    customer?: string;
  };
  alternateSkuList: string[];
}

interface Customer {
  id: string;
  name: string;
  code: string;
}

interface InternalTransferFormProps {
  visible: boolean;
  transfer?: InternalTransfer | null;
  warehouses: Warehouse[];
  products: Product[];
  customers?: Customer[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const InternalTransferForm: React.FC<InternalTransferFormProps> = ({
  visible,
  transfer,
  warehouses,
  products,
  customers = [],
  onSubmit,
  onCancel,
}) => {
    const { t } = useTranslation();
  const [form] = Form.useForm();
  const [items, setItems] = useState<TransferItem[]>([]);
  const [localCustomers, setLocalCustomers] = useState<Customer[]>([]);
  const [searchProducts, setSearchProducts] = useState<Product[]>([]);
  const [localWarehouses, setLocalWarehouses] = useState<Warehouse[]>([]);

  // 库位相关状态（与 Inbound 相同）
  const [warehouseLocations, setWarehouseLocations] = useState<Location[]>([]);
  const [locationTreeDataByWarehouse, setLocationTreeDataByWarehouse] = useState<Record<string, any[]>>({});
  const [locationLabelMapByWarehouse, setLocationLabelMapByWarehouse] = useState<Record<string, Record<string, string>>>({});
  const [locationsLoadingByWarehouse, setLocationsLoadingByWarehouse] = useState<Record<string, boolean>>({});
  // Store raw hierarchy data for rebuilding trees with inventory
  const [locationHierarchyByWarehouse, setLocationHierarchyByWarehouse] = useState<Record<string, any[]>>({});
  // Inventory by location: { `${warehouseId}-${productId}`: { locationCode: quantity } }
  const [inventoryByLocation, setInventoryByLocation] = useState<Record<string, Record<string, number>>>({});
  // Raw inventory records from API for building dropdown directly
  const [inventoryRawRecordsByLocation, setInventoryRawRecordsByLocation] = useState<Record<string, any[]>>({});
  const companyIdForTransfer = '1';

  // 初始化表單數據
  useEffect(() => {
    if (visible) {
      if (transfer) {
        // 編輯模式
        form.setFieldsValue({
          title: transfer.title,
          priority: transfer.priority,
          reason: transfer.reason,
          notes: transfer.notes,
        });
        // 將後端字段映射到表單內部結構，確保回顯
        const mapped = (transfer.items || []).map((it: any) => ({
          id: String(it.id),
          productId: String(it.productId || ''),
          productName: it.productName || '',
          productNameCN: it.productNameCN || '',
          sku: it.sku || '',
          quantity: Number(it.quantity) || 1,
          availableQuantity: (it.availableQuantity !== undefined && it.availableQuantity !== null) ? Number(it.availableQuantity) : (undefined as any),
          unit: it.unit || '個',
          fromLocation: it.fromLocation || '',
          toLocation: it.toLocation || '',
          isQuantityValid: it.isQuantityValid !== false,
          currentCustomer: it.currentCustomer || '',
          targetCustomer: it.targetCustomer || it.targetCustomerId || '',
          currentWarehouse: it.currentWarehouse || '',
          targetWarehouse: it.targetWarehouse || transfer.toWarehouse || '',
          remark: it.remark || '',
          // Lot number and expiry date fields
          lotNumber: it.lotNumber || '',
          expiryDate: it.expiryDate || '',
          targetLotNumber: it.targetLotNumber || '',
          targetExpiryDate: it.targetExpiryDate || '',
        }));
        setItems(mapped);
      } else {
        // 新增模式
        form.resetFields();
        setItems([]);
      }
    }
  }, [visible, transfer, form, warehouses]);


  // 懶載入客戶（若父層未提供）
  const ensureCustomersLoaded = async () => {
    try {
      if ((customers?.length || 0) > 0 || (localCustomers?.length || 0) > 0) return;
      const resp: any = await apiService.getCustomersV2();
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const mapped: Customer[] = rows.map((c: any) => ({
        id: String(c.id || c.customerId),
        name: c.name || c.customerName || '-',
        code: c.code || c.customerCode || '-'
      }));
      setLocalCustomers(mapped);
    } catch (_) {
      setLocalCustomers([]);
    }
  };

  // 懶載入倉庫（若父層未提供）
  const ensureWarehousesLoaded = async () => {
    try {
      if ((warehouses?.length || 0) > 0 || (localWarehouses?.length || 0) > 0) return;
      const companyId = localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
      const resp: any = await apiService.getWarehouses(companyId ? { companyId } : undefined);
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const mapped: Warehouse[] = rows.map((w: any) => ({
        value: String(w.id || w.warehouseId || w.code),
        label: w.name || w.warehouseName || w.label || '-',
        locations: Array.isArray(w.locations) ? w.locations : [],
        id: String(w.id || w.warehouseId || ''),
        code: w.code || w.warehouseCode || undefined,
      }));
      setLocalWarehouses(mapped);
    } catch (_) {
      setLocalWarehouses([]);
    }
  };

  // 加载库位数据（完整复制 Inbound 的逻辑）
  const fetchWarehouseLocations = useCallback(async (warehouseId: string) => {
    if (!warehouseId) {
      return;
    }

    // 如果已经加载过，直接返回
    if (locationTreeDataByWarehouse[warehouseId]) {
      return;
    }

    try {
      setLocationsLoadingByWarehouse(prev => ({ ...prev, [warehouseId]: true }));
      
      // 优先使用仓库 code
      const allWh = [...warehouses, ...localWarehouses];
      const wh = allWh.find(w => String(w.value) === String(warehouseId));
      const queryKey = (wh?.code && String(wh.code).trim().length > 0) ? String(wh.code) : String(warehouseId);
      
      const response: any = await apiService.getWarehouseLocations(warehouseId, {
        flat: false,
        companyId: companyIdForTransfer,
      });

      const hierarchy = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

      const formatLocationPath = (segments: string[]): string => {
        if (!Array.isArray(segments) || segments.length === 0) return '';

        if (segments.length === 1) {
          return segments[0];
        }

        if (segments.length === 2) {
          return segments.join('/');
        }

        const firstThree = segments.slice(0, 3).join('/');
        const rest = segments.slice(3).join('-');
        return rest ? `${firstThree}/${rest}` : firstThree;
      };

      const buildTree = (
        nodes: any[],
        parentCodes: string[] = [],
        labelMap: Record<string, string>
      ): any[] => {
        if (!Array.isArray(nodes)) return [];
        const sortedNodes = [...nodes].sort((a, b) => {
          const aLevel = Number(a.level ?? a.levels ?? 0);
          const bLevel = Number(b.level ?? b.levels ?? 0);
          if (aLevel !== bLevel) return aLevel - bLevel;
          const aCode = (a.locationCode || a.location_code || a.code || '').toString();
          const bCode = (b.locationCode || b.location_code || b.code || '').toString();
          return aCode.localeCompare(bCode, 'zh-Hant');
        });

        return sortedNodes.map((n: any) => {
          const code = (n.locationCode || n.location_code || n.code || '').toString();
          const name = (n.locationName || n.location_name || n.name || code).toString();
          const display = getInboundLocationDisplayConfig(n, t);
          const rawChildren = n.children || n.childLocations || n.locations || [];
          const currentCodes = [...parentCodes, name || code];
          const parentPrefix = formatLocationPath(parentCodes);
          const displayLabel = parentPrefix ? `${parentPrefix}/${name || code}` : (name || code);
          const valueKey = (n.id || code || name).toString();
          labelMap[valueKey] = displayLabel;
          const children = buildTree(rawChildren, currentCodes, labelMap);
          const searchText = [name, code, display.label, n.aisle, n.rack, n.shelf, n.bin]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return {
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {display.icon}
                <Text strong>{name || code}</Text>
                <Tag color={display.color}>{display.label}</Tag>
                {n.aisle && <Tag color="green">{t('通道:')} {n.aisle}</Tag>}
                {n.rack && <Tag color="orange">{t('貨架:')} {n.rack}</Tag>}
                {n.shelf && <Tag color="purple">{t('層:')} {n.shelf}</Tag>}
                {n.bin && <Tag color="cyan">{t('儲位:')} {n.bin}</Tag>}
                {n.capacity != null && n.usedCapacity != null && (
                  <Text type="secondary"> {n.usedCapacity}/{n.capacity}</Text>
                )}
              </div>
            ),
            key: (n.id || code || name).toString(),
            value: (n.id || code || name).toString(),
            displayLabel,
            selectable: display.type === 'BIN',
            disabled: display.type !== 'BIN' && children.length === 0,
            searchText,
            children,
          };
        });
      };

      const labelMap: Record<string, string> = {};
      const treeData = buildTree(hierarchy, [], labelMap);

      // Store raw hierarchy for rebuilding with inventory data later
      setLocationHierarchyByWarehouse(prev => ({ ...prev, [warehouseId]: hierarchy }));
      setLocationTreeDataByWarehouse(prev => ({ ...prev, [warehouseId]: treeData }));
      setLocationLabelMapByWarehouse(prev => ({ ...prev, [warehouseId]: labelMap }));

      const flatten = (nodes: any[], acc: Location[] = [], whId: string = warehouseId): Location[] => {
        const list = Array.isArray(nodes)
          ? [...nodes].sort((a, b) => {
              const aLevel = Number(a.level ?? 0);
              const bLevel = Number(b.level ?? 0);
              if (aLevel !== bLevel) return aLevel - bLevel;
              const aCode = (a.locationCode || a.location_code || a.code || '').toString();
              const bCode = (b.locationCode || b.location_code || b.code || '').toString();
              return aCode.localeCompare(bCode, 'zh-Hant');
            })
          : [];

        list.forEach((n) => {
          const code = n.locationCode || n.location_code || n.code || '';
          const name = n.locationName || n.location_name || n.name || code;
          acc.push({ id: n.id || code, name, code, warehouseId: whId });
          const childNodes = n.children || n.childLocations || n.locations;
          if (Array.isArray(childNodes) && childNodes.length) flatten(childNodes, acc, whId);
        });
        return acc;
      };
      setWarehouseLocations(flatten(hierarchy));
    } catch (error) {
      console.error('Failed to fetch warehouse locations:', error);
      message.error(t('載入倉庫位置失敗'));
      setLocationTreeDataByWarehouse(prev => ({ ...prev, [warehouseId]: [] }));
      setLocationLabelMapByWarehouse(prev => ({ ...prev, [warehouseId]: {} }));
    } finally {
      setLocationsLoadingByWarehouse(prev => ({ ...prev, [warehouseId]: false }));
    }
  }, [companyIdForTransfer, warehouses, localWarehouses, locationTreeDataByWarehouse]);

  // Fetch inventory for a product in a warehouse to show stock quantities by location
  // Uses the new stock-by-location API that queries inbound_order_items
  const fetchInventoryForProductWarehouse = useCallback(async (productId: string, warehouseId: string) => {
    const cacheKey = `${warehouseId}-${productId}`;
    const cachedData = inventoryByLocation[cacheKey];
    // Only skip if we have actual stock data (object with keys), not an empty cache
    const hasValidCache = cachedData && Object.keys(cachedData).length > 0;
    console.log('fetchInventoryForProductWarehouse called:', { productId, warehouseId, cacheKey, cachedData, hasValidCache });
    if (hasValidCache) return; // Already fetched with data

    try {
      console.log('Fetching stock by location from API...', { productId, warehouseId });
      // Fetch stock by location records for this product
      const response: any = await apiService.getStockByLocation({
        productId,
        warehouseId
      });
      console.log('API response:', response);

      const inventoryRecords = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];

      // Build a map of locationId -> quantity (primary key for tree node matching)
      // Include special key for warehouse-level stock (未分配庫位)
      const locationQtyMap: Record<string, number> = {};
      let warehouseLevelQty = 0;

      inventoryRecords.forEach((inv: any) => {
        const locId = inv.locationId || inv.location_id || '';
        const fullPath = inv.fullPath || inv.full_path || '';
        const locCode = inv.locationCode || inv.location_code || '';
        const qty = Number(inv.availableQuantity ?? inv.totalQuantity ?? inv.quantity ?? 0);

        if (!locId && !fullPath && !locCode) {
          // This is warehouse-level stock (no location assigned)
          warehouseLevelQty += qty;
        } else {
          // Primary key: locationId (most reliable for tree node matching)
          if (locId) {
            locationQtyMap[locId] = (locationQtyMap[locId] || 0) + qty;
          }

          // Secondary key: fullPath (e.g., "ZONE/3/FR21/02/01")
          if (fullPath) {
            locationQtyMap[fullPath] = (locationQtyMap[fullPath] || 0) + qty;
          }
        }
      });

      // Add warehouse-level stock as special key
      if (warehouseLevelQty > 0) {
        locationQtyMap['__WAREHOUSE_LEVEL__'] = warehouseLevelQty;
      }

      console.log('Final locationQtyMap:', locationQtyMap);
      console.log('Raw inventory records:', inventoryRecords);

      setInventoryByLocation(prev => ({ ...prev, [cacheKey]: locationQtyMap }));
      // Also store raw records for building dropdown directly
      setInventoryRawRecordsByLocation(prev => ({ ...prev, [cacheKey]: inventoryRecords }));
    } catch (error) {
      console.error('Failed to fetch inventory for product:', error);
      // Don't cache errors - allow retry on next dropdown open
      // The user can retry by closing and reopening the dropdown
    }
  }, [inventoryByLocation]);

  // Build tree with inventory quantities displayed
  const buildTreeWithInventory = useCallback((
    nodes: any[],
    parentCodes: string[] = [],
    labelMap: Record<string, string>,
    inventoryMap: Record<string, number> = {}
  ): any[] => {
    if (!Array.isArray(nodes)) return [];

    const formatLocationPath = (segments: string[]): string => {
      if (!Array.isArray(segments) || segments.length === 0) return '';
      if (segments.length === 1) return segments[0];
      if (segments.length === 2) return segments.join('/');
      const firstThree = segments.slice(0, 3).join('/');
      const rest = segments.slice(3).join('-');
      return rest ? `${firstThree}/${rest}` : firstThree;
    };

    const sortedNodes = [...nodes].sort((a, b) => {
      const aLevel = Number(a.level ?? a.levels ?? 0);
      const bLevel = Number(b.level ?? b.levels ?? 0);
      if (aLevel !== bLevel) return aLevel - bLevel;
      const aCode = (a.locationCode || a.location_code || a.code || '').toString();
      const bCode = (b.locationCode || b.location_code || b.code || '').toString();
      return aCode.localeCompare(bCode, 'zh-Hant');
    });

    return sortedNodes.map((n: any) => {
      const code = (n.locationCode || n.location_code || n.code || '').toString();
      const name = (n.locationName || n.location_name || n.name || code).toString();
      const display = getInboundLocationDisplayConfig(n, t);
      const rawChildren = n.children || n.childLocations || n.locations || [];
      const currentCodes = [...parentCodes, name || code];
      const parentPrefix = formatLocationPath(parentCodes);
      const displayLabel = parentPrefix ? `${parentPrefix}/${name || code}` : (name || code);
      const valueKey = (n.id || code || name).toString();
      labelMap[valueKey] = displayLabel;
      const children = buildTreeWithInventory(rawChildren, currentCodes, labelMap, inventoryMap);

      // Get stock quantity for this location
      // Primary match: by location ID (n.id), secondary: by fullPath or displayLabel
      const nodeId = (n.id || '').toString();
      const stockQty = inventoryMap[nodeId] || inventoryMap[displayLabel] || undefined;

      if (Object.keys(inventoryMap).length > 0 && display.type === 'BIN') {
        console.log('Tree node matching:', { nodeId, code, name, displayLabel, stockQty, inventoryMapKeys: Object.keys(inventoryMap).slice(0, 5) });
      }

      const searchText = [name, code, display.label, n.aisle, n.rack, n.shelf, n.bin]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {display.icon}
            <Text strong>{name || code}</Text>
            <Tag color={display.color}>{display.label}</Tag>
            {n.aisle && <Tag color="green">{t('通道:')} {n.aisle}</Tag>}
            {n.rack && <Tag color="orange">{t('貨架:')} {n.rack}</Tag>}
            {n.shelf && <Tag color="purple">{t('層:')} {n.shelf}</Tag>}
            {n.bin && <Tag color="cyan">{t('儲位:')} {n.bin}</Tag>}
            {stockQty !== undefined && stockQty > 0 && (
              <Tag color="blue">{t('庫存:')} {stockQty}</Tag>
            )}
            {n.capacity != null && n.usedCapacity != null && (
              <Text type="secondary"> {n.usedCapacity}/{n.capacity}</Text>
            )}
          </div>
        ),
        key: valueKey,
        value: valueKey,
        displayLabel,
        selectable: display.type === 'BIN',
        disabled: display.type !== 'BIN' && children.length === 0,
        searchText,
        children,
      };
    });
  }, []);

  // Filter tree to only show locations with stock (for source location selection)
  // Keeps parent nodes (ZONE, AISLE, RACK, SHELF) only if they have descendants with stock
  const filterTreeToStockOnly = useCallback((
    nodes: any[],
    inventoryMap: Record<string, number> = {}
  ): any[] => {
    if (!Array.isArray(nodes) || nodes.length === 0) return [];
    if (Object.keys(inventoryMap).length === 0) return []; // No inventory data, show nothing

    return nodes.map((node: any) => {
      // First, recursively filter children
      const filteredChildren = filterTreeToStockOnly(node.children || [], inventoryMap);

      // Check if this node has stock
      const nodeKey = node.key || node.value || '';
      const displayLabel = node.displayLabel || '';
      const hasStock = (inventoryMap[nodeKey] && inventoryMap[nodeKey] > 0) ||
                       (inventoryMap[displayLabel] && inventoryMap[displayLabel] > 0);

      // Include node if:
      // 1. It has stock directly (selectable BIN with stock)
      // 2. It has children with stock (parent node)
      if (hasStock || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren
        };
      }
      return null;
    }).filter(Boolean);
  }, []);

  // 產品即時搜尋（使用現有後端 API）
  const searchProductsByKeyword = async (keyword?: string) => {
    try {
      const companyId = localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
      let resp: any;
      try {
        resp = await apiService.getProductsV2(keyword ? { search: keyword, companyId } : (companyId ? { companyId } : undefined));
      } catch (_) {
        // v2 不可用時，回退舊版 /products
        resp = await apiService.getProducts(keyword ? { search: keyword, companyId } : (companyId ? { companyId } : undefined));
      }
      const rows: any[] = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.products)
        ? resp.products
        : Array.isArray(resp)
        ? resp
        : [];
      const mapped: Product[] = rows.map((p: any) => ({
        id: String(p.id || p.productId || p.itemId || p.code || p.itemCode),
        name: p.name || p.productName || p.itemName || '-',
        nameCN: p.nameCN || p.productNameCN || p.cnName || p.itemName || '-',
        sku: p.sku || p.productSku || p.itemCode || p.code || '-',
        availableStock: p.availableStock || {},
        unit: p.unit || p.uom || '個',
        currentLocation: {
          warehouse: p.currentLocation?.warehouse || '-',
          location: p.currentLocation?.location || '-',
          customer: p.currentLocation?.customer || '-',
        },
        alternateSkuList: p.alternateSkuList || [],
      }));
      setSearchProducts(mapped);
    } catch (_) {
      setSearchProducts([]);
    }
  };

  // 處理目標倉庫庫位更新
  const handleTargetWarehouseChange = (itemIndex: number, warehouseId: string) => {
    const newItems = [...items];
    newItems[itemIndex] = {
      ...newItems[itemIndex],
      targetWarehouse: warehouseId,
      toLocation: '' // 重置庫位選擇
    };
    setItems(newItems);
    // 不在这里加载库位，改为懒加载（在打开下拉框时加载）
  };

  // 打開庫位下拉時，若尚未載入則嘗試載入

  // 獲取可用庫存
  const getAvailableStock = (productId: string, warehouseId: string): number | undefined => {
    const product = products.find(p => p.id === productId);
    if (!product) return undefined;
    const val = (product.availableStock || ({} as any))[warehouseId];
    if (val === undefined || val === null) return undefined;
    const num = Number(val);
    return Number.isFinite(num) ? num : undefined;
  };

  // 添加商品行項目
  const addItem = () => {
    const newItem: TransferItem = {
      id: `item_${Date.now()}`,
      productId: '',
      productName: '',
      productNameCN: '',
      sku: '',
      quantity: 1,
      availableQuantity: undefined as any,
      unit: '個',
      fromLocation: '',
      toLocation: '',
      isQuantityValid: true,
      currentCustomer: '',
      targetCustomer: '',
      currentWarehouse: '',
      targetWarehouse: ''
    };
    setItems([...items, newItem]);
  };

  // 刪除商品行項目
  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // 更新商品行項目
  const updateItem = (index: number, field: keyof TransferItem, value: any) => {
    const newItems = [...items];
    // 轉移數量加上邊界控制與即時校驗
    if (field === 'quantity') {
      const availRaw = newItems[index].availableQuantity;
      const available = typeof availRaw === 'number' ? availRaw : undefined;
      const raw = Number(value || 1);
      const clamped = Math.max(1, available !== undefined ? Math.min(raw, available) : raw);
      newItems[index] = { ...newItems[index], quantity: clamped };
      newItems[index].isQuantityValid = available !== undefined ? clamped <= available : true;
    } else {
      if (field === 'productName') {
        const prevCN = newItems[index].productNameCN || '';
        newItems[index] = {
          ...newItems[index],
          productName: String(value || ''),
          productNameCN: prevCN ? prevCN : String(value || ''),
          sku: newItems[index].sku || '',
          availableQuantity: newItems[index].availableQuantity,
          isQuantityValid: true,
        } as any;
      } else {
        newItems[index] = { ...newItems[index], [field]: value } as any;
      }
    }
    
    setItems(newItems);
  };

  // 處理商品選擇
  const handleProductSelect = (index: number, value: string, option?: any) => {
    console.log('Product selection triggered:', { index, value, option });
    
    // 優先從option中獲取product數據，如果沒有則通過value查找
    let product = option?.product;
    if (!product) {
      product = products.find(p => 
        p.id === value || 
        p.name === value || 
        p.sku === value ||
        p.nameCN === value
      );
    }
    
    console.log('Found product:', product);
    
    if (product) {
      // 自動填充當前位置信息（從產品的當前位置）；若缺失，從 availableStock 估算來源倉庫
      const currentLocation = product.currentLocation;
      let currentWarehouse = '';
      let currentLocationName = '';
      let currentCustomer = '';
      let availableQuantity: number | undefined = undefined;

      if (currentLocation && currentLocation.warehouse) {
        currentWarehouse = currentLocation.warehouse;
        currentLocationName = currentLocation.location;
        currentCustomer = currentLocation.customer || '';
        availableQuantity = getAvailableStock(product.id, currentWarehouse);
      }

      // 若沒有當前庫位信息或無法取得可用量，嘗試從可用庫存映射推斷
      if (availableQuantity === undefined) {
        const entries = Object.entries(product.availableStock || {});
        if (entries.length > 0) {
          // 優先選擇有正庫存的倉庫；否則選第一個鍵
          const positive = entries.find(([, qty]) => Number(qty) > 0);
          const fallback = positive || entries[0];
          if (fallback) {
            const [whId, qty] = fallback;
            currentWarehouse = currentWarehouse || String(whId);
            availableQuantity = Number(qty);
          }
        }
      }

      console.log('Current location info:', { currentWarehouse, currentLocationName, currentCustomer, availableQuantity });

      // 批量更新商品信息
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        productId: product.id,
        productName: product.name,
        productNameCN: product.nameCN,
        sku: product.sku,
        unit: product.unit,
        availableQuantity: availableQuantity ?? 0,
        currentCustomer: currentCustomer,
        currentWarehouse: currentWarehouse,
        fromLocation: currentLocationName,
        // Autofilled from the product, not picked from the dropdown — no lot yet.
        fromLocationKey: '',
        isQuantityValid: (availableQuantity === undefined) ? true : ((newItems[index].quantity || 1) <= availableQuantity)
      };

      setItems(newItems);
      console.log('Updated items:', newItems);
    } else {
      // 若找不到產品，作為自由輸入商品處理
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        productId: newItems[index].productId || `manual_${Date.now()}`,
        productName: String(value || ''),
        productNameCN: String(value || ''),
        sku: newItems[index].sku || '',
        unit: newItems[index].unit || '個',
        availableQuantity: newItems[index].availableQuantity,
        currentCustomer: newItems[index].currentCustomer || '',
        currentWarehouse: newItems[index].currentWarehouse || '',
        fromLocation: newItems[index].fromLocation || '',
        isQuantityValid: true,
      } as any;
      setItems(newItems);
    }
  };

  // 產品搜索選項
  const getProductOptions = () => {
    const source = (searchProducts.length > 0 ? searchProducts : products);
    return source.map(product => ({
      value: product.name,
      label: `${product.name} (${product.sku}) - ${product.nameCN}${product.alternateSkuList?.length ? ` [${product.alternateSkuList.join(', ')}]` : ''}`,
      product: product,
      key: product.id
    }));
  };

  // 提交表單
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (items.length === 0) {
        message.error(t('請至少添加一個商品'));
        return;
      }

      // 檢查每個商品是否選擇了產品和目標倉庫
      const incompleteItems = items.filter(item => 
        !item.productName || !item.targetWarehouse || !item.toLocation
      );
      if (incompleteItems.length > 0) {
        message.error(t('有 {{count}} 個商品未完成設定，請檢查產品選擇、目標倉庫和目標庫位', { count: incompleteItems.length }));
        return;
      }

      // 檢查是否有庫存問題
      const stockIssues = items.filter(item => !item.isQuantityValid);
      if (stockIssues.length > 0) {
        Modal.confirm({
          title: t('庫存警告'),
          content: t('有 {{count}} 個商品庫存不足，是否繼續提交？', { count: stockIssues.length }),
          onOk: () => {
            submitTransfer(values);
          },
        });
      } else {
        submitTransfer(values);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const submitTransfer = (values: any) => {
    const totalItems = items.length;
    const totalQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const estimatedValue = items.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      return sum + ((Number(item.quantity) || 0) * 100); // 假設單價為100，實際應從產品數據獲取
    }, 0);

    // 統計涉及的倉庫
    const involvedWarehouses = new Set();
    items.forEach(item => {
      if (item.currentWarehouse) involvedWarehouses.add(item.currentWarehouse);
      if (item.targetWarehouse) involvedWarehouses.add(item.targetWarehouse);
    });

    const transferData = {
      ...values,
      items: items.map(it => ({
        id: it.id,
        productId: it.productId || undefined,
        productName: it.productName,
        productNameCN: it.productNameCN,
        sku: it.sku,
        quantity: it.quantity,
        availableQuantity: it.availableQuantity,
        unit: it.unit,
        fromLocation: it.fromLocation,
        toLocation: it.toLocation,
        isQuantityValid: it.isQuantityValid,
        remark: (it as any).remark,
        targetCustomerId: it.targetCustomer || undefined,
        targetCustomer: undefined,
        currentCustomer: undefined,
        currentWarehouse: undefined,
        targetWarehouse: it.targetWarehouse || undefined,
        // Lot number and expiry date fields
        lotNumber: it.lotNumber || undefined,
        expiryDate: it.expiryDate || undefined,
        targetLotNumber: it.targetLotNumber || undefined,
        targetExpiryDate: it.targetExpiryDate || undefined,
      })),
      totalItems,
      totalQuantity,
      estimatedValue,
      involvedWarehouses: Array.from(involvedWarehouses),
      status: '待審批' as TransferStatus
    };

    onSubmit(transferData);
  };

  // 商品表格列定義
  const itemColumns: ColumnsType<TransferItem> = [
    {
      title: t('商品名稱'),
      key: 'productName',
      width: 200,
      render: (_, record, index) => (
        <AutoComplete
          style={{ width: '100%' }}
          options={getProductOptions()}
          value={record.productName}
          onSelect={(value, option) => handleProductSelect(index, value, option)}
          onChange={(value) => updateItem(index, 'productName', value)}
          placeholder={t('搜尋商品名稱或SKU')}
          onSearch={(text) => searchProductsByKeyword(text)}
          onDropdownVisibleChange={(open) => { if (open && searchProducts.length === 0) searchProductsByKeyword(); }}
          filterOption={(inputValue, option) => {
            if (!inputValue || !option) return true;
            const input = inputValue.toLowerCase();
            const product = option.product;
            return (
              option.label.toLowerCase().includes(input) ||
              product.name.toLowerCase().includes(input) ||
              product.nameCN.toLowerCase().includes(input) ||
              product.sku.toLowerCase().includes(input)
            );
          }}
          allowClear
        />
      ),
    },
    {
      title: t('中文名稱'),
      key: 'productNameCN',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.productNameCN}</div>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {t('SKU:')} {record.sku}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: t('批次號'),
      key: 'lotNumber',
      width: 100,
      render: (_, record) => (
        <Typography.Text>
          {record.lotNumber || '-'}
        </Typography.Text>
      ),
    },
    {
      title: t('到期日'),
      key: 'expiryDate',
      width: 100,
      render: (_, record) => {
        if (!record.expiryDate) return <Typography.Text>-</Typography.Text>;
        const date = new Date(record.expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        const isExpired = daysUntilExpiry < 0;
        const isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
        return (
          <Typography.Text type={isExpired ? 'danger' : isExpiringSoon ? 'warning' : undefined}>
            {date.toLocaleDateString('zh-TW')}
          </Typography.Text>
        );
      },
    },
    {
      title: t('當前客戶'),
      key: 'currentCustomer',
      width: 120,
      render: (_, record) => (
        <div>
          <Typography.Text style={{ fontSize: '12px' }}>
            {record.currentCustomer || t('無')}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: t('來源倉庫'),
      key: 'currentWarehouse',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.currentWarehouse || undefined}
          onChange={(value) => {
            const newItems = [...items];
            newItems[index] = {
              ...newItems[index],
              currentWarehouse: value,
              fromLocation: '', // Reset source location when warehouse changes
              fromLocationKey: ''
            };
            setItems(newItems);
            // Load locations for this warehouse
            if (value) {
              fetchWarehouseLocations(value);
            }
          }}
          placeholder={t('選擇來源倉庫')}
          style={{ width: '100%' }}
          showSearch
          allowClear
          optionFilterProp="children"
          notFoundContent={t('無倉庫')}
          onDropdownVisibleChange={(open) => { if (open) ensureWarehousesLoaded(); }}
        >
          {(warehouses.length > 0 ? warehouses : localWarehouses).map(warehouse => (
            <Option key={warehouse.value} value={warehouse.value}>
              <Space>
                <HomeOutlined />
                {warehouse.label}
              </Space>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: t('來源庫位'),
      key: 'fromLocation',
      width: 200,
      render: (_, record, index) => {
        const warehouseId = record.currentWarehouse;
        const productId = record.productId;
        const cacheKey = warehouseId && productId ? `${warehouseId}-${productId}` : '';
        const inventoryMap = cacheKey ? (inventoryByLocation[cacheKey] || {}) : {};
        const hierarchy = warehouseId ? (locationHierarchyByWarehouse[warehouseId] || []) : [];

        console.log('Render fromLocation:', {
          warehouseId,
          productId,
          cacheKey,
          inventoryMapKeys: Object.keys(inventoryMap),
          hierarchyLength: hierarchy.length,
          hasTreeData: warehouseId ? !!locationTreeDataByWarehouse[warehouseId] : false
        });

        // Build flat list directly from inventory data (only locations with stock)
        // This is simpler and more accurate than filtering the warehouse tree
        const labelMap: Record<string, string> = {};
        let treeData: any[] = [];

        // Get raw inventory records from the stored data
        const rawInventoryRecords = inventoryRawRecordsByLocation[cacheKey] || [];

        // Maps each composite option key back to the exact inventory row it was
        // built from, so onChange applies the lot the user actually clicked
        // rather than the first row that happens to share the location.
        const recordBySourceKey: Record<string, any> = {};

        if (rawInventoryRecords.length > 0) {
          // Build options directly from inventory records
          rawInventoryRecords.forEach((inv: any) => {
            const locId = inv.locationId || inv.location_id || '';
            const fullPath = inv.fullPath || inv.full_path || '';
            const qty = Number(inv.availableQuantity ?? inv.totalQuantity ?? inv.quantity ?? 0);
            const lotNumber = inv.lotNumber || inv.lot_number || '';

            if (!locId && !fullPath) {
              // Warehouse-level stock - handled separately below
              return;
            }

            // Create a simple selectable node for each location with stock
            const displayLabel = fullPath || locId;
            const lotInfo = lotNumber ? t(' (批次: {{lotNumber}})', { lotNumber }) : '';
            // Warehouse-level stock the backend re-labelled as KC/KC has no
            // location UUID, so fall back to the path to keep keys non-empty.
            const optionKey = makeSourceKey(locId || fullPath, lotNumber);
            labelMap[optionKey] = displayLabel;
            recordBySourceKey[optionKey] = inv;

            treeData.push({
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <HomeOutlined style={{ color: '#52c41a' }} />
                  <Text strong>{shortLocation(displayLabel)}</Text>
                  <Tag color="blue">{t('庫存:')} {qty}</Tag>
                  {lotNumber && <Tag color="cyan">{t('批次:')} {lotNumber}</Tag>}
                </div>
              ),
              key: optionKey,
              value: optionKey,
              displayLabel: displayLabel + lotInfo,
              selectable: true,
              searchText: `${displayLabel} ${lotNumber}`.toLowerCase(),
              children: [],
            });
          });
        } else if (Object.keys(inventoryMap).length > 0) {
          // Fallback: use inventoryMap if raw records not available
          Object.entries(inventoryMap).forEach(([key, qty]) => {
            if (key === '__WAREHOUSE_LEVEL__' || qty <= 0) return;
            const optionKey = makeSourceKey(key, '');
            labelMap[optionKey] = key;
            treeData.push({
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <HomeOutlined style={{ color: '#52c41a' }} />
                  <Text strong>{key}</Text>
                  <Tag color="blue">{t('庫存:')} {qty}</Tag>
                </div>
              ),
              key: optionKey,
              value: optionKey,
              displayLabel: key,
              selectable: true,
              searchText: key.toLowerCase(),
              children: [],
            });
          });
        }

        // Add "未分配庫位" node for warehouse-level stock
        const warehouseLevelQty = inventoryMap['__WAREHOUSE_LEVEL__'] || 0;
        if (warehouseLevelQty > 0) {
          const unassignedNode = {
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <WarningOutlined style={{ color: '#faad14' }} />
                <Text strong style={{ color: '#faad14' }}>{t('未分配庫位')}</Text>
                <Tag color="warning">{t('庫存:')} {warehouseLevelQty}</Tag>
              </div>
            ),
            key: '__WAREHOUSE_LEVEL__',
            value: '__WAREHOUSE_LEVEL__',
            displayLabel: '未分配庫位',
            selectable: true,
            searchText: '未分配庫位 unassigned warehouse level',
            children: [],
          };
          labelMap['__WAREHOUSE_LEVEL__'] = '未分配庫位';
          treeData = [unassignedNode, ...treeData];
        }

        const isLoading = warehouseId
          ? (locationsLoadingByWarehouse[warehouseId] || false)
          : false;

        // Determine placeholder based on state
        const hasInventoryData = Object.keys(inventoryMap).length > 0;
        const hasNoStock = hasInventoryData && treeData.length === 0;
        const getPlaceholder = () => {
          if (isLoading) return t('載入中...');
          if (!warehouseId) return t('請先選擇來源倉庫');
          if (!productId) return t('請先選擇產品');
          if (hasNoStock) return t('此產品在該倉庫無庫存');
          return t('選擇來源庫位');
        };

        return (
          <TreeSelect
            // Prefer the composite key so the picked row stays highlighted;
            // fall back to the stored path for transfers loaded for editing.
            value={record.fromLocationKey || record.fromLocation || undefined}
            onChange={(value) => {
              // Store the display label instead of the ID for better readability
              const existingLabelMap = warehouseId
                ? (locationLabelMapByWarehouse[warehouseId] || {})
                : {};
              const { locationId: selectedLocationId } = parseSourceKey(value || '');
              const displayLabel = labelMap[value]
                || existingLabelMap[value]
                || existingLabelMap[selectedLocationId]
                || selectedLocationId;

              // Get the available quantity for the selected location
              let locationQty = inventoryMap[selectedLocationId] || 0;

              // Extract lotNumber and expiryDate from matching record
              let selectedLotNumber = '';
              let selectedExpiryDate = '';

              // The option key carries the lot, so this resolves to the exact row
              // that was clicked instead of the first one sharing the location.
              const matchingRecord = value === '__WAREHOUSE_LEVEL__'
                ? rawInventoryRecords.find((inv: any) => {
                    const locId = inv.locationId || inv.location_id || '';
                    return !locId && !inv.fullPath && !inv.full_path;
                  })
                : recordBySourceKey[value];

              if (matchingRecord) {
                locationQty = Number(matchingRecord.availableQuantity ?? matchingRecord.totalQuantity ?? matchingRecord.quantity ?? 0);
                selectedLotNumber = matchingRecord.lotNumber || matchingRecord.lot_number || '';
                selectedExpiryDate = matchingRecord.expiryDate || matchingRecord.expiry_date || '';
              }

              console.log('Location selected:', { value, displayLabel, locationQty, selectedLotNumber, selectedExpiryDate, inventoryMap });

              // Update fromLocation, availableQuantity, lotNumber, expiryDate
              // Note: targetLotNumber and targetExpiryDate are populated from target location selection, not source
              const newItems = [...items];
              newItems[index] = {
                ...newItems[index],
                fromLocation: displayLabel,
                fromLocationKey: value || '',
                availableQuantity: locationQty,
                isQuantityValid: (newItems[index].quantity || 1) <= locationQty,
                lotNumber: selectedLotNumber,
                expiryDate: selectedExpiryDate,
              };
              setItems(newItems);
            }}
            placeholder={getPlaceholder()}
            style={{ width: '100%' }}
            disabled={!warehouseId || isLoading}
            treeData={treeData}
            treeLine
            showSearch
            allowClear
            treeNodeLabelProp="displayLabel"
            treeNodeFilterProp="searchText"
            filterTreeNode={(input, treeNode) => {
              const searchText = ((treeNode as any)?.searchText || '').toLowerCase();
              return searchText.includes(input.toLowerCase());
            }}
            onDropdownVisibleChange={(open) => {
              if (open && warehouseId) {
                fetchWarehouseLocations(warehouseId);
                // Also fetch inventory for this product in this warehouse
                if (productId) {
                  fetchInventoryForProductWarehouse(productId, warehouseId);
                }
              }
            }}
            dropdownStyle={{
              maxHeight: 400,
              minWidth: 500,
              overflow: 'auto',
              overflowX: 'auto',
              overflowY: 'auto'
            }}
            notFoundContent={isLoading ? t('載入中...') : (warehouseId ? t('無可用庫位') : t('請先選擇來源倉庫'))}
          />
        );
      },
    },
    {
      title: t('轉移數量'),
      key: 'quantity',
      width: 120,
      render: (_, record, index) => (
        <div>
          <InputNumber
            min={1}
            max={record.availableQuantity || undefined}
            value={record.quantity}
            onChange={(value) => updateItem(index, 'quantity', value || 1)}
            style={{ width: '100%' }}
            status={record.isQuantityValid ? undefined : 'error'}
            addonAfter={record.unit}
          />
          {!record.isQuantityValid && typeof record.availableQuantity === 'number' && (
            <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
              {t('可用:')} {record.availableQuantity}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('目標客戶'),
      key: 'targetCustomer',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.targetCustomer}
          onChange={(value) => updateItem(index, 'targetCustomer', value)}
          placeholder={t('選擇目標客戶')}
          style={{ width: '100%' }}
          allowClear
          showSearch
          optionFilterProp="children"
          notFoundContent={t('無客戶')}
          onDropdownVisibleChange={(open) => { if (open) ensureCustomersLoaded(); }}
        >
          {((customers?.length || 0) + (localCustomers?.length || 0)) === 0 && (
            <Option key="_empty" value="" disabled>
              {t('無客戶')}
                                  </Option>
          )}
          {(customers && customers.length > 0 ? customers : localCustomers).map(customer => (
            <Option key={customer.id} value={customer.id}>
              {customer.name} ({customer.code})
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: t('目標倉庫'),
      key: 'targetWarehouse',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.targetWarehouse}
          onChange={(value) => handleTargetWarehouseChange(index, value)}
          placeholder={t('選擇目標倉庫')}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
          notFoundContent={t('無倉庫')}
        >
          {warehouses.map(warehouse => (
            <Option key={warehouse.value} value={warehouse.value}>
              <Space>
                <HomeOutlined />
                {warehouse.label}
              </Space>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: t('目標庫位'),
      key: 'toLocation',
      width: 200,
      render: (_, record, index) => {
        const warehouseId = record.targetWarehouse;
        const productId = record.productId;
        const cacheKey = warehouseId && productId ? `${warehouseId}-${productId}` : '';
        const inventoryMap = cacheKey ? (inventoryByLocation[cacheKey] || {}) : {};
        const hierarchy = warehouseId ? (locationHierarchyByWarehouse[warehouseId] || []) : [];

        // Build tree with inventory quantities
        const labelMap: Record<string, string> = {};
        const treeData = hierarchy.length > 0
          ? buildTreeWithInventory(hierarchy, [], labelMap, inventoryMap)
          : (warehouseId ? (locationTreeDataByWarehouse[warehouseId] || []) : []);

        const isLoading = warehouseId
          ? (locationsLoadingByWarehouse[warehouseId] || false)
          : false;

        return (
          <TreeSelect
            value={record.toLocation || undefined}
            onChange={(value) => {
              // Store the location ID (UUID) for backend to use directly
              // The displayLabel is only for UI display purposes
              const existingLabelMap = warehouseId
                ? (locationLabelMapByWarehouse[warehouseId] || {})
                : {};
              const displayLabel = labelMap[value] || existingLabelMap[value] || value;

              // Fetch target location's lot number and expiry date from inventory records
              const targetCacheKey = warehouseId && productId ? `${warehouseId}-${productId}` : '';
              const targetRawRecords = inventoryRawRecordsByLocation[targetCacheKey] || [];

              let targetLotNumber = '';
              let targetExpiryDate = '';

              if (targetRawRecords.length > 0) {
                const matchingRecord = targetRawRecords.find((inv: any) => {
                  const locId = inv.locationId || inv.location_id || '';
                  if (value === '__WAREHOUSE_LEVEL__') {
                    return !locId && !inv.fullPath && !inv.full_path;
                  }
                  return locId === value;
                });
                if (matchingRecord) {
                  targetLotNumber = matchingRecord.lotNumber || matchingRecord.lot_number || '';
                  targetExpiryDate = matchingRecord.expiryDate || matchingRecord.expiry_date || '';
                }
              }

              console.log('Target location selected:', { value, displayLabel, targetLotNumber, targetExpiryDate });

              // Store the display label for UI readability, backend will resolve the location
              const newItems = [...items];
              newItems[index] = {
                ...newItems[index],
                toLocation: displayLabel, // Store display label for readability
                targetLotNumber: targetLotNumber,
                targetExpiryDate: targetExpiryDate,
              };
              setItems(newItems);
            }}
            placeholder={isLoading ? t('載入中...') : (warehouseId ? t('選擇目標庫位') : t('請先選擇目標倉庫'))}
            style={{ width: '100%' }}
            disabled={!warehouseId || isLoading}
            treeData={treeData}
            treeLine
            showSearch
            allowClear
            treeNodeLabelProp="displayLabel"
            treeNodeFilterProp="searchText"
            filterTreeNode={(input, treeNode) => {
              const searchText = ((treeNode as any)?.searchText || '').toLowerCase();
              return searchText.includes(input.toLowerCase());
            }}
            onDropdownVisibleChange={(open) => {
              if (open && warehouseId) {
                fetchWarehouseLocations(warehouseId);
                // Also fetch inventory for this product in this warehouse
                if (productId) {
                  fetchInventoryForProductWarehouse(productId, warehouseId);
                }
              }
            }}
            dropdownStyle={{
              maxHeight: 400,
              minWidth: 500,
              overflow: 'auto',
              overflowX: 'auto',
              overflowY: 'auto'
            }}
            notFoundContent={isLoading ? t('載入中...') : (warehouseId ? t('無可用庫位') : t('請先選擇目標倉庫'))}
          />
        );
      },
    },
    {
      title: t('目標批次號'),
      key: 'targetLotNumber',
      width: 120,
      render: (_, record, index) => (
        <Input
          value={record.targetLotNumber}
          onChange={(e) => updateItem(index, 'targetLotNumber', e.target.value)}
          placeholder={t('批次號')}
        />
      ),
    },
    {
      title: t('目標到期日'),
      key: 'targetExpiryDate',
      width: 130,
      render: (_, record, index) => (
        <DatePicker
          value={record.targetExpiryDate ? dayjs(record.targetExpiryDate) : null}
          onChange={(date) => updateItem(index, 'targetExpiryDate', date ? date.toISOString() : '')}
          placeholder={t('到期日')}
          format="YYYY-MM-DD"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('備註'),
      key: 'remark',
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.remark}
          onChange={(e) => updateItem(index, 'remark', e.target.value)}
          placeholder={t('備註')}
        />
      ),
    },
    {
      title: t('狀態'),
      key: 'status',
      width: 80,
      render: (_, record) => (
        record.isQuantityValid ? 
          <Tag color="green" icon={<CheckCircleOutlined />}>{t('正常')}</Tag> : 
          <Tag color="red" icon={<WarningOutlined />}>{t('庫存不足')}</Tag>
      ),
    },
    {
      title: t('操作'),
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_, record, index) => (
        <Popconfirm
          title={t('確定要刪除此商品嗎？')}
          onConfirm={() => removeItem(index)}
          okText={t('確定')}
          cancelText={t('取消')}
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined />
          {transfer ? t('編輯轉移請求') : t('新增轉移請求')}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('取消')}
                      </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {transfer ? t('更新') : t('創建')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          priority: 'medium',
          status: '待審批',
        }}
      >
        <Alert
          message={t('轉移請求說明')}
          description={t('請填寫轉移請求的基本信息，然後添加需要轉移的商品。每個商品可分別選擇源倉庫和目標倉庫。系統會自動檢查庫存可用性。')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* 基本信息 */}
        <Card title={t('基本信息')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('轉移標題')}
                name="title"
                rules={[{ required: true, message: t('請輸入轉移標題') }]}
              >
                <Input placeholder={t('請描述此次轉移的目的')} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label={t('優先級')}
                name="priority"
                rules={[{ required: true, message: t('請選擇優先級') }]}
              >
                <Select>
                  <Option value="urgent">{t('緊急')}</Option>
                  <Option value="high">{t('高')}</Option>
                  <Option value="medium">{t('中')}</Option>
                  <Option value="low">{t('低')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={t('轉移原因')}
                name="reason"
                rules={[{ required: true, message: t('請填寫轉移原因') }]}
              >
                <TextArea rows={2} placeholder={t('請詳細說明此次轉移的原因和目的')} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 工作流程說明 */}
        <Card title={t('轉移工作流程')} style={{ marginBottom: 16 }}>
          <Alert
            message={t('新的轉移流程')}
            description={
              <div>
                <div style={{ marginBottom: 8 }}>{t('請按以下順序操作：')}</div>
                <div>1. <strong>{t('選擇產品')}</strong> {t('- 搜尋並選擇要轉移的產品')}</div>
                <div>2. <strong>{t('查看當前位置')}</strong> {t('- 系統自動顯示產品當前的客戶和倉庫位置')}</div>
                <div>3. <strong>{t('選擇目標位置')}</strong> {t('- 選擇目標客戶、目標倉庫和庫位')}</div>
                <div>4. <strong>{t('設定數量')}</strong> {t('- 輸入要轉移的數量')}</div>
              </div>
            }
            type="info"
            showIcon
          />
        </Card>

        {/* 商品清單 */}
        <Card 
          title={
            <Space>
              <BoxPlotOutlined />
              {t('轉移商品清單')}
                                        {items.length > 0 && (
                <Tag color="blue">{items.length} {t('種商品')}</Tag>
              )}
            </Space>
          }
          extra={
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={addItem}
            >
              {t('添加商品')}
                              </Button>
          }
          style={{ marginBottom: 16 }}
        >
          {items.length === 0 ? (
            <Alert
              message={t('尚未添加商品')}
              description={t('請點擊「添加商品」按鈕來添加需要轉移的商品')}
              type="warning"
              showIcon
            />
          ) : (
            <Table
              dataSource={items}
              columns={itemColumns}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1800 }}
              size="small"
            />
          )}

          {items.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space size="large">
                <Typography.Text>
                  {t('總商品種類:')} <Tag color="blue">{items.length}</Tag>
                </Typography.Text>
                <Typography.Text>
                  {t('總轉移數量:')} <Tag color="green">{items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)}</Tag>
                </Typography.Text>
                <Typography.Text>
                  {t('庫存問題:')} <Tag color={items.some(item => !item.isQuantityValid) ? "red" : "green"}>
                    {items.filter(item => !item.isQuantityValid).length} {t('種商品')}
                                                        </Tag>
                </Typography.Text>
              </Space>
            </div>
          )}
        </Card>

        {/* 備註 */}
        <Card title={t('備註信息')}>
          <Form.Item
            label={t('備註')}
            name="notes"
          >
            <TextArea rows={3} placeholder={t('其他備註信息...')} />
          </Form.Item>
        </Card>
      </Form>
    </Modal>
  );
};

export default InternalTransferForm;